import { useState, useMemo } from "react";
import CommonCard from "../../../../global/card";
import SOURCES from "../../../../../utils/constants";
import { useLocation } from "react-router-dom";
import { useGenericQuery } from "../../../../../queries/use-generic-query";
import { QUERY_KEYS } from "../../../../../lib/query-constants";
import useDebounce from "../../../../../utils/customHooks";
import EnhancedLowActivityUsersList from "./enhancedLowActivityUsersList";
import CommonPagination from "../../../../global/pagination";
import CommonSkeletonLoader from "../../../../global/loading";
import CommonCardView from "../../../../global/cardView";
import EnhancedLowActivityUsersCardView from "./enhancedLowActivityUsersCardView";
import { useGenericMutation } from "../../../../../queries/use-generic-mutation";
import { useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import NoRecordFound from "../../../../global/noRecordFound";
import CommonDrawer from "../../../../global/drawer";
import EnhancedLowActivityUsersDetails from "./enhancedLowActivityUsersDetails";

const EnhancedLowActivityUsers = () => {
    const [lowActivityUsersSearch, setLowActivityUsersSearch] = useState("");
    const [lowActivityUsersViewType, setLowActivityUsersViewType] = useState(() => {
        return localStorage.getItem("lowActivityUsersViewType") || "GRID";
    });
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });
    const [showDetails, setShowDetails] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        24
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const debouncedQuery = useDebounce(lowActivityUsersSearch, 500)
    const keyParams = useMemo(() => ({
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    }), [pagination.offset, pagination.limit, sort.sortType, sort.sortOrder]);

    const queryClient = useQueryClient();
    const { mutate: updateStatus } = useGenericMutation({
        endpoint: "/admin/status",
        method: "POST",
        onSuccess: () => {
            message.success("User status updated successfully");
            queryClient.invalidateQueries(QUERY_KEYS.lowActivityUsers.all);
        },
        onError: () => {
            message.error("Failed to update user status");
        }
    });

    const handleStatusChange = (userId, currentStatus) => {
        const newStatus = currentStatus === 'y' ? 'n' : 'y';
        updateStatus({
            id: userId,
            status: newStatus,
            changeStatusOf: "User"
        });
    };

    const handleViewDetails = (record) => {
        setSelectedUser(record);
        setShowDetails(true);
    };

    const { data, isLoading } = useGenericQuery({
        endpoint: "/admin/lowActivityUsers",
        queryKey: QUERY_KEYS.lowActivityUsers.lists({ ...keyParams }),
        queryParams: { ...keyParams },
        enabled: !debouncedQuery
    })


    const { data: searchData, isInitialLoading: searchDataLoading } = useGenericQuery({
        endpoint: "/admin/lowActivityUsers/search",
        queryKey: QUERY_KEYS.lowActivityUsers.lists({ q: debouncedQuery }),
        queryParams: { q: debouncedQuery },
        enabled: !!debouncedQuery && debouncedQuery.length >= 2,
    })

    const isLoadingList = isLoading || searchDataLoading;

    const finalizedData = useMemo(() => {
        if (debouncedQuery && searchData) {
            return searchData?.users || [];
        }
        return data?.users || [];
    }, [data, searchData, debouncedQuery]);

    const totalRecords = useMemo(() => {
        if (debouncedQuery && searchData) return searchData?.users?.length || 0;
        return data?.count || 0;
    }, [data, searchData, debouncedQuery]);


    return (
        <CommonCard
            isSearch={{
                show: true,
                name: "lowActivityUsers",
                placeholder: "Search low activity users...",
                value: lowActivityUsersSearch,
                setSearchInput: setLowActivityUsersSearch,
            }}
            isListGridView={{
                show: true,
                setViewType: setLowActivityUsersViewType,
                viewType: lowActivityUsersViewType,
                viewFor: "lowActivityUsersViewType"
            }}
            isLoading={{
                show: isLoadingList,
                loaderCmp: <CommonSkeletonLoader type={lowActivityUsersViewType === "LIST" ? 'table' : 'card'} />
            }}
        >
            {finalizedData?.length > 0 ? (
                <>
                    {lowActivityUsersViewType === "LIST" ? (
                        <EnhancedLowActivityUsersList
                            userList={finalizedData}
                            isLoading={isLoadingList}
                            sort={sort}
                            setSort={setSort}
                            onStatusChange={handleStatusChange}
                            onRowClick={handleViewDetails}
                        />
                    ) : (
                        <CommonCardView>
                            {finalizedData.map((record, idx) => (
                                <EnhancedLowActivityUsersCardView
                                    key={record._id || idx}
                                    record={record}
                                    onStatusChange={handleStatusChange}
                                    onCardClick={handleViewDetails}
                                />
                            ))}
                        </CommonCardView>
                    )}
                </>
            ) : (
                !isLoadingList && <NoRecordFound message="No low activity users found." />
            )}

            {
                !debouncedQuery &&
                totalRecords > SOURCES.DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={finalizedData?.length || 0}
                        paginationFor="Low Activity Users"
                        className="mt-4"
                    />
                )
            }

            {showDetails && (
                <CommonDrawer
                    open={showDetails}
                    setOpen={() => setShowDetails(false)}
                    title="User Details"
                    width="1000px"
                    children={
                        <EnhancedLowActivityUsersDetails
                            userDetail={selectedUser}
                        />
                    }
                />
            )}
        </CommonCard>
    )
}

export default EnhancedLowActivityUsers